import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Form,
  Row,
  Col,
  Pagination,
  Offcanvas,
  Badge
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { agentService } from '../../services/agentService';
import UserEditModal from './UserEditModal';
import { isAdmin } from '../../services/auth';
import toast from 'react-hot-toast';

import { useTranslation } from 'react-i18next';

const currencyMap = {
  'None': '', 'USD': '$', 'GBP': '£', 'BTC': '₿', 'EUR': '€', 'CHF': '₣',
  'KRW': '₩', 'TRY': '₺', 'INR': '₹', 'CNY': '¥', 'THB': '฿',
  'RUB': '₽', 'UAH': '₴', 'BDT': '৳', 'JPY': '¥', 'ILS': '₪',
  'AED': 'د.إ', 'SAR': 'ر.س'
};

const UserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userCategory, setUserCategory] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const [filters, setFilters] = useState({
    userId: '',
    mobileNumber: '',
    startTime: '',
    endTime: ''
  });

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await agentService.getUsers({
        page,
        limit: 10,
        ...filters,
        status: userCategory
      });
      if (response.success) {
        setUsers(response.users);
        setTotalPages(response.pagination.pages);
        setCurrentPage(response.pagination.page);
      }
    } catch (error) {
      toast.error(t('error_fetch_users'));
    } finally {
      setLoading(false);
    }
  }, [filters, userCategory, t]);

  useEffect(() => {
    fetchUsers(1);
  }, [fetchUsers]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      userId: '',
      mobileNumber: '',
      startTime: '',
      endTime: ''
    });
    setUserCategory('all');
  };

  const handlePageChange = (page) => {
    fetchUsers(page);
  };

  const openDetails = (user) => {
    navigate(`/user-details/${user.id}`);
  };

  return (
    <div className="majestic-page-wrapper">

      {/* Top Action Bar */}
      <div className="d-flex justify-content-between mb-3">
        {isAdmin() && (
          <Button
            variant="primary"
            size="sm"
            className="px-4 d-flex align-items-center gap-2"
            onClick={() => {
              setSelectedUser(null);
              setShowEditModal(true);
            }}
          >
            <i className="bi bi-person-plus"></i>
            {t('create_new_user', 'Create New User')}
          </Button>
        )}
      </div>

      {/* Desktop Filter Section */}
      <div className="majestic-card filter-bar mb-3">
        <div className="d-flex align-items-center mb-3 w-100">
          <span className="text-secondary small me-3 fw-bold" style={{ whiteSpace: 'nowrap' }}>{t('user_categories')}</span>
          <div className="btn-group">
            <Button
              variant={userCategory === 'all' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setUserCategory('all')}
            >{t('cat_all')}</Button>
            <Button
              variant={userCategory === 'acting' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setUserCategory('acting')}
            >{t('cat_acting')}</Button>
          </div>
        </div>

        <Row className="g-3 align-items-center w-100">
          <Col xs={12} md="auto">
            <div className="d-flex align-items-center">
              <span className="form-label mb-0 text-secondary small me-2" style={{ minWidth: '80px' }}>{t('user_id')}</span>
              <Form.Control
                id="userId"
                type="text"
                name="userId"
                placeholder={t('user_id')}
                value={filters.userId}
                onChange={handleFilterChange}
                className="w-100"
                style={{ minWidth: '150px' }}
              />
            </div>
          </Col>
          <Col xs={12} md="auto">
            <div className="d-flex align-items-center">
              <span className="form-label mb-0 text-secondary small me-2" style={{ minWidth: '80px' }}>{t('mobile_number')}</span>
              <Form.Control
                id="mobileNumber"
                type="text"
                name="mobileNumber"
                placeholder=""
                value={filters.mobileNumber}
                onChange={handleFilterChange}
                className="w-100"
                style={{ minWidth: '150px' }}
              />
            </div>
          </Col>
          <Col xs={12} md="auto" className="ms-auto text-end">
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="primary" size="sm" className="px-3" onClick={() => fetchUsers(1)}>{t('query')}</Button>
              <Button variant="secondary" size="sm" className="px-3" onClick={clearFilters}>{t('reset')}</Button>
              <Button variant="secondary" size="sm" className="px-3">{t('export')}</Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* Mobile Filter Trigger Button */}
      <div className="filter-drawer-btn d-md-none" onClick={() => setShowFilterDrawer(true)}>
        <i className="bi bi-funnel"></i>
      </div>

      {/* Mobile Filter Drawer */}
      <Offcanvas
        show={showFilterDrawer}
        onHide={() => setShowFilterDrawer(false)}
        placement="bottom"
        className="majestic-card border-top border-secondary h-auto rounded-top-4"
        style={{ maxHeight: '85vh', background: 'var(--bg-card)' }}
      >
        <Offcanvas.Header closeButton className="border-bottom border-light border-opacity-10">
          <Offcanvas.Title className="fw-bold fs-5">{t('filter_options')}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-4">
          <Form className="d-flex flex-column gap-3">
            <div className="mb-2">
              <label className="text-secondary small mb-2 d-block">{t('user_categories')}</label>
              <div className="d-flex gap-2">
                <Button
                  variant={userCategory === 'all' ? 'primary' : 'outline-secondary'}
                  className="flex-grow-1"
                  onClick={() => setUserCategory('all')}
                >{t('cat_all')}</Button>
                <Button
                  variant={userCategory === 'acting' ? 'primary' : 'outline-secondary'}
                  className="flex-grow-1"
                  onClick={() => setUserCategory('acting')}
                >{t('cat_acting')}</Button>
              </div>
            </div>
            <div>
              <label className="text-secondary small mb-1">{t('user_id')}</label>
              <Form.Control
                id="userId-mobile"
                size="lg"
                type="text"
                name="userId"
                placeholder={t('enter_user_id')}
                value={filters.userId}
                onChange={handleFilterChange}
                className="bg-dark text-white border-secondary"
              />
            </div>
            <div>
              <label className="text-secondary small mb-1">{t('mobile_number')}</label>
              <Form.Control
                id="mobileNumber-mobile"
                size="lg"
                type="text"
                name="mobileNumber"
                placeholder={t('enter_mobile')}
                value={filters.mobileNumber}
                onChange={handleFilterChange}
                className="bg-dark text-white border-secondary"
              />
            </div>
            <div className="d-flex gap-2 mt-3">
              <Button variant="secondary" size="lg" className="flex-grow-1" onClick={clearFilters}>{t('reset')}</Button>
              <Button variant="primary" size="lg" className="flex-grow-1 fw-bold" onClick={() => { fetchUsers(1); setShowFilterDrawer(false); }}>{t('query')}</Button>
            </div>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Mobile Card List View */}
      <div className="mobile-card-list">
        {loading ? (
          <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
        ) : users.length === 0 ? (
          <div className="text-center py-5 text-secondary">{t('no_data')}</div>
        ) : (
          users.map(user => (
            <div key={user.id} className="majestic-mobile-card" onClick={() => openDetails(user)}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                  <div className="rounded-circle bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                    <i className="bi bi-person-fill fs-5"></i>
                  </div>
                  <div>
                    <div className="fw-bold text-white">{user.username}</div>
                    <div className="text-secondary extra-small">ID: {user.id.slice(-8).toUpperCase()}</div>
                  </div>
                </div>
                <i className="bi bi-chevron-right text-secondary"></i>
              </div>

              <div className="card-row">
                <span className="card-label ms-1">{t('balance')}</span>
                <span className="card-value text-primary fw-bold fs-5">
                  {(user.currency && currencyMap[user.currency] !== undefined) ? currencyMap[user.currency] : (user.currency === 'None' ? '' : '$')}
                  {user.accountBalance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="card-row">
                <span className="card-label ms-1">{t('email')}</span>
                <span className="card-value small">{user.email || '-'}</span>
              </div>
              <div className="card-row">
                <span className="card-label ms-1">{t('mobile_number')}</span>
                <span className="card-value small">{user.mobileNumber || '-'}</span>
              </div>
              <div className="card-row">
                <span className="card-label ms-1">{t('user_categories')}</span>
                <span className="card-value">
                  <Badge bg={user.isSimulated ? 'secondary' : 'info'} className="fw-normal">
                    {user.isSimulated ? t('simulated_account') : t('normal_account')}
                  </Badge>
                </span>
              </div>
              <div className="card-row">
                <span className="card-label ms-1">{t('designated_pnl')}</span>
                <span className="card-value">
                  <Badge bg={user.winLossControl?.isActive ? 'warning' : 'secondary'} text={user.winLossControl?.isActive ? 'dark' : 'white'} className="fw-normal">
                    {user.winLossControl?.isActive ? (user.winLossControl.controlLevel === 'high' ? t('win_strategy') : t('deficit_strategy')) : t('with_the_market')}
                  </Badge>
                </span>
              </div>
              <div className="card-row">
                <span className="card-label ms-1">{t('creation_time')}</span>
                <span className="card-value small text-secondary">{new Date(user.createdAt).toLocaleString('en-GB', { hour12: false })}</span>
              </div>

              <div className="card-row border-top border-light border-opacity-10 pt-2 mt-2">
                <span className="card-label ms-1">{t('last_active')}</span>
                <span className="card-value text-secondary extra-small">{new Date(user.createdAt).toLocaleString('en-GB', { hour12: false })}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Data Table */}
      <div className="majestic-card p-0 majestic-table-container">
        <Table hover className="border-0 mb-0">
          <thead>
            <tr>
              <th>{t('user_id')}</th>
              <th>{t('username')}</th>
              <th>{t('user_nickname')}</th>
              <th>{t('invitation_code')}</th>
              <th>{t('inviter_superior')}</th>
              <th>{t('credit_score')}</th>
              <th>{t('win_loss_control')}</th>
              <th>{t('balance')}</th>
              <th>{t('frozen_balance')}</th>
              <th>{t('commission_balance')}</th>
              <th>{t('registration_time')}</th>
              <th className="text-end">{t('operate')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="12" className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('loading')}</span>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="12" className="text-center py-5 text-secondary">
                  {t('no_data')}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id.slice(-8).toUpperCase()}</td>
                  <td style={{ color: 'var(--text-primary)' }}>{user.username}</td>
                  <td>{user.email.split('@')[0]}</td>
                  <td>{user.referCode || 'N/A'}</td>
                  <td>{user.assignedAgent?.username || 'None'}</td>
                  <td>{user.creditScore ?? 0}</td>
                  <td>
                    {(!user.winLossControl || !user.winLossControl.isActive) ? t('cat_all') :
                      user.winLossControl.controlLevel.toUpperCase()}
                  </td>
                  <td className="text-blue fw-bold">
                    {(user.currency && currencyMap[user.currency] !== undefined) ? currencyMap[user.currency] : (user.currency === 'None' ? '' : '$')}
                    {user.accountBalance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td>0.00</td>
                  <td>0.00</td>
                  <td>{new Date(user.createdAt).toLocaleString('en-GB', { hour12: false })}</td>
                  <td className="text-end">
                    <span
                      className="fw-bold"
                      style={{ cursor: 'pointer', color: 'var(--primary-blue)' }}
                      onClick={() => openDetails(user)}
                    >{t('details')}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-end mt-3 mb-5 mb-md-0">
        <Pagination size="sm">
          <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
          {Array.from({ length: totalPages }, (_, i) => (
            <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => handlePageChange(i + 1)}>
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>

      {/* Keep Modals for functionality */}
      <UserEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        user={selectedUser}
        onSuccess={() => fetchUsers(currentPage)}
      />
    </div >
  );
};

export default UserManagement;